import React from 'react';
import { PlaybookAdherence, SalesIntelligence, getBuyingSignalColor } from '../../adherence/types';
import SummarySectionCard from './SummarySectionCard';
import { ProgressBar, scoreTone } from '../../ui/ProgressBar';

interface AdvanceDealSummaryCardProps {
  playbook?: PlaybookAdherence;
  salesIntelligence?: SalesIntelligence;
  closeAttemptTimestamp?: string;
  onViewDetails: () => void;
  onViewInTimeline?: () => void;
}

const AdvanceDealSummaryCard: React.FC<AdvanceDealSummaryCardProps> = ({
  playbook,
  salesIntelligence,
  closeAttemptTimestamp,
  onViewDetails,
  onViewInTimeline,
}) => {
  const hasGeneric = !!playbook?.deal_progression_generic;
  const hasPlaybook = playbook?.was_used && playbook?.deal_progression_coverage;
  const coveragePct = playbook?.deal_progression_coverage?.coverage_pct;
  const genericScore = playbook?.deal_progression_generic?.score;
  const closingSignal = salesIntelligence?.closing_signal;
  const closingScore = salesIntelligence?.closing_signal_score;

  const displayScore = hasPlaybook && coveragePct != null ? coveragePct : genericScore;

  const badgeColor =
    displayScore == null
      ? 'bg-surface-subtle text-ink-muted border border-hairline'
      : displayScore >= 70
      ? 'bg-success-tint text-success border border-success'
      : displayScore >= 40
      ? 'bg-warning-tint text-warning border border-warning'
      : 'bg-error-tint text-error border border-error';

  const statusBadge = displayScore != null ? (
    <span className={`text-[11px] font-bold px-2 py-0.5 rounded ${badgeColor}`}>{displayScore}%</span>
  ) : undefined;

  const coachingText = playbook?.deal_progression_generic?.coaching;
  const coachingItems: string[] = [];
  if (coachingText) coachingItems.push(coachingText);

  const missingMsg =
    !playbook && !salesIntelligence
      ? 'Deal advancement analysis not available. Re-analyze this session.'
      : !hasGeneric && !closingSignal
      ? 'Re-analyze this session to see deal advancement analysis.'
      : undefined;

  const resultContent = (
    <div className="space-y-3">
      {/* Closing signal */}
      {closingSignal && (
        <div className="bg-surface-subtle border border-hairline rounded-lg px-3 py-2 flex items-center gap-2">
          <span className="text-[10px] font-semibold text-ink-muted uppercase tracking-wider flex-shrink-0">
            Closing Signal
          </span>
          <span
            className={`text-xs font-semibold px-2 py-0.5 rounded capitalize ${getBuyingSignalColor(closingSignal)}`}
          >
            {closingSignal.replace(/_/g, ' ')}
          </span>
          {typeof closingScore === 'number' && (
            <span className="text-[10px] text-ink-muted ml-auto">({Math.round(closingScore)}%)</span>
          )}
        </div>
      )}

      {/* Playbook deal progression coverage */}
      {hasPlaybook && playbook.deal_progression_coverage && (
        <div className="bg-info-tint border border-info rounded-lg p-3">
          <div className="flex items-center justify-between mb-2">
            <p className="text-[10px] font-semibold text-info uppercase tracking-wider">Playbook Coverage</p>
            <span className="text-xs font-bold text-ink">
              {playbook.deal_progression_coverage.total_delivered}/{playbook.deal_progression_coverage.total_available} points
            </span>
          </div>
          <ProgressBar value={playbook.deal_progression_coverage.coverage_pct} tone={scoreTone(playbook.deal_progression_coverage.coverage_pct)} />
          {playbook.deal_progression_coverage.progression_summary && (
            <p className="text-xs text-ink mt-2">{playbook.deal_progression_coverage.progression_summary}</p>
          )}
          {(playbook.deal_progression_coverage.missed_progression_points?.length ?? 0) > 0 && (
            <div className="mt-2 pt-2 border-t border-info">
              <p className="text-[10px] text-error font-semibold mb-1">Missed points:</p>
              <ul className="space-y-0.5">
                {playbook.deal_progression_coverage.missed_progression_points!.slice(0, 2).map((point, idx) => (
                  <li key={idx} className="text-[11px] text-error">• {point}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Generic deal progression analysis */}
      {playbook?.deal_progression_generic && (
        <div className="bg-surface-subtle border border-hairline rounded-lg p-3">
          <div className="flex items-center justify-between mb-2">
            <p className="text-[10px] font-semibold text-ink-muted uppercase tracking-wider">Generic Analysis</p>
            <span className={`text-[11px] font-bold px-1.5 py-0.5 rounded ${badgeColor}`}>{genericScore}%</span>
          </div>
          <p className="text-xs text-ink-secondary">{playbook.deal_progression_generic.summary}</p>
          {(playbook.deal_progression_generic.what_worked || playbook.deal_progression_generic.what_missed) && (
            <div className="mt-2 space-y-1 pt-2 border-t border-hairline">
              {playbook.deal_progression_generic.what_worked && (
                <p className="text-[11px] text-success">
                  <span className="font-semibold">Worked: </span>{playbook.deal_progression_generic.what_worked}
                </p>
              )}
              {playbook.deal_progression_generic.what_missed && (
                <p className="text-[11px] text-error">
                  <span className="font-semibold">Missed: </span>{playbook.deal_progression_generic.what_missed}
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {!closingSignal && !hasGeneric && !hasPlaybook && (
        <p className="text-xs text-ink-muted">No deal advancement data available for this session.</p>
      )}
    </div>
  );

  return (
    <SummarySectionCard
      title="Advance Deal"
      statusBadge={statusBadge}
      resultContent={resultContent}
      coachingItems={coachingItems.length > 0 ? coachingItems : undefined}
      sectionKey="advance_deal"
      onViewDetails={onViewDetails}
      onViewInTimeline={closeAttemptTimestamp ? onViewInTimeline : undefined}
      missingDataMessage={missingMsg}
      className="col-span-full"
    />
  );
};

export default AdvanceDealSummaryCard;
